"use client"

import type { ReactNode } from "react"
import { motion } from "framer-motion"
import { useConvexConnectionState, useQuery } from "convex/react"
import { CountUp } from "@/components/home/count-up"
import { Sparkline } from "@/components/home/sparkline"
import { ConvexUrlProvider } from "@/components/providers/convex-url-provider"
import { fmtRivCompact } from "@/lib/rivyu-metrics-format"
import {
  pickCurrentActiveUsers,
  rivyuConvexBugBountyStats,
  rivyuConvexGlobalStats,
  rivyuConvexIdeaPadStats,
  rivyuConvexInvestorStats,
  type RivyuBugBountyStatsPublic,
  type RivyuGlobalStatsPublic,
  type RivyuIdeaPadStatsPublic,
  type RivyuInvestorStatsPublic,
} from "@/lib/rivyu-convex-queries"

export type StatsSectionProps = {
  /** Prefer passing from the server page so `.env` updates apply without a stale client bundle. */
  convexUrl?: string
}

/** TGE target date — surfaced with an audit/legal-entity dependency note across
 *  hero, stats and roadmap. The countdown UI has been removed; this constant
 *  remains so any legacy import keeps compiling. */
const TGE_TARGET_LABEL = "19 Sept 2026 (subject to audit + legal entity)"

/** Static placeholder shown only when no Convex URL is configured (preview/dev).
 *  Renders a fixed sample figure — it must not imitate a live on-chain feed. */
function SampleMinersValue() {
  return <CountUp to={12847} dur={1100} />
}

/** Preview meta row shown when Convex URL is unset — clearly labelled as sample data. */
function SampleDataMeta() {
  return (
    <div className="meta">
      <span>
        <span className="lit">◇</span> SAMPLE DATA
      </span>
      <span>PREVIEW MODE</span>
      <span>NOT LIVE</span>
    </div>
  )
}

function LiveConvexMeta({ updatedAt }: { updatedAt?: number }) {
  const state = useConvexConnectionState()
  const label = state.isWebSocketConnected
    ? "LIVE"
    : state.hasEverConnected
      ? "RECONNECTING"
      : "CONNECTING"
  const ts =
    updatedAt && updatedAt > 0
      ? new Date(updatedAt).toISOString().slice(11, 19) + "Z"
      : "—"
  return (
    <div className="meta">
      <span>
        <span className="lit">◆</span> CONVEX
      </span>
      <span>{label}</span>
      <span>UPDATED {ts}</span>
    </div>
  )
}

type CommunityProgramsProps =
  | { mode: "offline" }
  | {
      mode: "live"
      ideas: RivyuIdeaPadStatsPublic | undefined
      bugs: RivyuBugBountyStatsPublic | undefined
    }

/** Grid copy and figures from `new reference/site/page-home.jsx` StatsSection. */
function StatsNumbersGrid({
  headMeta,
  minersPill,
  minersBig,
  uniqueStakers,
  communityPrograms,
}: {
  headMeta: ReactNode
  minersPill: ReactNode
  minersBig: ReactNode
  /** Live unique staking wallets; undefined = offline or still loading. */
  uniqueStakers?: number
  communityPrograms: CommunityProgramsProps
}) {
  const isLive = communityPrograms.mode === "live"
  const ideas = isLive ? communityPrograms.ideas : undefined
  const bugs = isLive ? communityPrograms.bugs : undefined
  const ideasLoading = isLive && ideas === undefined
  const bugsLoading = isLive && bugs === undefined
  return (
    <motion.section
      className="st-wrap"
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.55, ease: [0, 0, 0.2, 1] }}
    >
      <div className="st-head">
        <h2>
          By the <span className="accent">Numbers_</span>
        </h2>
        {headMeta}
      </div>
      <div className="st-grid">
        <div className="st-card span-2">
          <div className="label">
            <span>// MINING_ALLOCATION</span>
            <span className="pill live">◆ LIVE</span>
          </div>
          <div className="big">
            <CountUp to={30} />
            <span className="unit">%</span>
          </div>
          <div className="note">
            PRE-TGE FAIR DISTRIBUTION VIA TASKS &amp; ON-CHAIN CLAIMS.
          </div>
          <div className="st-pulse-bar">
            {Array.from({ length: 28 }).map((_, i) => (
              <div
                key={i}
                className="bar"
                style={{
                  animationDelay: `${i * 50}ms`,
                  height: `${30 + (i % 5) * 6 + Math.abs(Math.sin(i)) * 18}px`,
                }}
              />
            ))}
          </div>
          <div className="footrow">
            <span>300,000,000 RIVYU EARMARKED</span>
            <span className="ok">◆ STREAMING</span>
          </div>
        </div>

        <div className="st-card">
          <div className="label">
            <span>// FOUNDERS @ TGE</span>
            <span className="pill">VEST_LOCK</span>
          </div>
          <div className="big amber">
            <CountUp to={0} />
            <span className="unit">%</span>
          </div>
          <div className="note">
            ZERO TEAM TOKENS UNLOCKED AT TGE.
            <br />
            120M LOCKED FOR 12 MONTHS.
          </div>
          <div className="footrow">
            <span>UNLOCK: TGE + 12mo</span>
            <span className="ok">◆ VEST LOCK</span>
          </div>
        </div>

        <div className="st-card">
          <div className="label">
            <span>// MAX_STAKING_APY</span>
            <span className={`pill${uniqueStakers !== undefined ? " live" : ""}`}>
              {uniqueStakers !== undefined ? "◆ LIVE" : "365d POOL"}
            </span>
          </div>
          <div className="big cyan">
            <CountUp to={60} />
            <span className="unit">%</span>
          </div>
          <div className="note">
            ABSOLUTE ZERO — 365-DAY LOCK.
            <br />
            FLEX POOL FROM 5% APY UPWARDS.
            {uniqueStakers !== undefined ? (
              <>
                <br />
                <strong>{uniqueStakers.toLocaleString("en-US")}</strong> UNIQUE STAKER WALLETS (ACTIVE STAKE).
              </>
            ) : null}
          </div>
          <div className="footrow">
            <span>{uniqueStakers !== undefined ? "LIVE FROM CONVEX" : "4 TIERED POOLS"}</span>
            <span className="ok">◆ STAKING_LIVE</span>
          </div>
        </div>

        <div className="st-card">
          <Sparkline w={140} h={60} color="#8fbfa8" />
          <div className="label">
            <span>// MINERS_ONLINE</span>
            {minersPill}
          </div>
          <div className="big" style={{ fontSize: 64 }}>
            {minersBig}
          </div>
          <div className="note">
            UNIQUE ACTIVE WALLETS LAST 24H.
            <br />
            SYNCED FROM ON-CHAIN ACTIVITY.
          </div>
          <div className="footrow">
            <span>ROLLING 24H WINDOW</span>
            <span className="ok">◆ CONVEX</span>
          </div>
        </div>

        <div className="st-card">
          <div className="label">
            <span>// TOKEN_STANDARD</span>
            <span className="pill">TEP-74</span>
          </div>
          <div className="big" style={{ fontSize: 56, lineHeight: 1.05 }}>
            JETTON
          </div>
          <div className="note">
            NATIVE TEP-74 ON TON.
            <br />
            FULL DEX &amp; WALLET INTEROP.
          </div>
          <div className="footrow">
            <span>CONTRACT TBD AT TGE</span>
            <span className="ok">◆ TEP-74</span>
          </div>
        </div>

        <div className="st-card">
          <Sparkline w={140} h={60} color="#00e5ff" />
          <div className="label">
            <span>// TGE_TARGET</span>
            <span className="pill" style={{ color: "var(--cyan)", borderColor: "var(--cyan)" }}>
              SCHEDULED
            </span>
          </div>
          <div className="big cyan" style={{ fontSize: 40, lineHeight: 1.1 }}>
            19 SEP 2026
            <span className="unit" style={{ display: "block", fontSize: 14, marginTop: 6 }}>
              TARGET DATE
            </span>
          </div>
          <div className="footrow">
            <span>SUBJECT TO AUDIT + LEGAL ENTITY</span>
            <span style={{ color: "var(--cyan)" }}>◆ TARGET</span>
          </div>
        </div>

        <div className="st-card">
          <div className="label">
            <span>// IDEA_PAD</span>
            <span className={`pill${isLive ? " live" : ""}`}>{isLive ? "◆ LIVE" : "OFFLINE"}</span>
          </div>
          <div className="big" style={{ fontSize: 72 }}>
            {!isLive ? (
              "—"
            ) : ideasLoading ? (
              <span className="text-[var(--text-dim)]">···</span>
            ) : (
              <CountUp to={ideas!.totalSubmitted} dur={1000} />
            )}
          </div>
          <div className="note">
            COMMUNITY IDEAS IN THE DAPP — <strong>APPROVED</strong> COUNTS ANY IDEA WITH APPROVAL TIMESTAMP (INCLUDES
            SHIPPED).
          </div>
          <div className="footrow">
            {!isLive || ideasLoading ? (
              <span>SET NEXT_PUBLIC_CONVEX_URL FOR LIVE TOTALS</span>
            ) : (
              <>
                <span>
                  APPR {ideas!.approved.toLocaleString("en-US")} · IMPL {ideas!.implemented.toLocaleString("en-US")} ·
                  REJ {ideas!.rejected.toLocaleString("en-US")}
                </span>
                <span className="ok">VOTE {ideas!.inVoting.toLocaleString("en-US")}</span>
              </>
            )}
          </div>
        </div>

        <div className="st-card">
          <div className="label">
            <span>// BUG_BOUNTY</span>
            <span className={`pill${isLive ? " live" : ""}`}>{isLive ? "◆ LIVE" : "OFFLINE"}</span>
          </div>
          <div className="big" style={{ fontSize: 72 }}>
            {!isLive ? (
              "—"
            ) : bugsLoading ? (
              <span className="text-[var(--text-dim)]">···</span>
            ) : (
              <CountUp to={bugs!.totalReports} dur={1000} />
            )}
          </div>
          <div className="note">
            REPORTS FILED IN-APP — RIV <strong>PAID OUT</strong> SUMS REWARDS CREDITED AFTER ON-CHAIN POOL VERIFICATION.
            <br />
            <strong>AWARDED</strong> IS TOTAL OWED ACROSS RESOLVED REPORTS.
          </div>
          <div className="footrow">
            {!isLive || bugsLoading ? (
              <span>SET NEXT_PUBLIC_CONVEX_URL FOR LIVE TOTALS</span>
            ) : (
              <>
                <span>
                  RES {bugs!.resolved.toLocaleString("en-US")} · CONF {bugs!.confirmed.toLocaleString("en-US")} · REJ{" "}
                  {bugs!.rejected.toLocaleString("en-US")}
                </span>
                <span className="ok">
                  RIV {fmtRivCompact(bugs!.rivRewardsAwarded)} OWED · {fmtRivCompact(bugs!.rivRewardsPaidOut)} PAID
                </span>
              </>
            )}
          </div>
        </div>
      </div>
    </motion.section>
  )
}

function StatsSectionStatic() {
  return (
    <StatsNumbersGrid
      headMeta={<SampleDataMeta />}
      minersPill={<span className="pill">SAMPLE</span>}
      minersBig={<SampleMinersValue />}
      communityPrograms={{ mode: "offline" }}
    />
  )
}

function StatsSectionLiveInner() {
  const global = useQuery(rivyuConvexGlobalStats, {}) as RivyuGlobalStatsPublic | undefined
  const investor = useQuery(rivyuConvexInvestorStats, {}) as RivyuInvestorStatsPublic | null | undefined
  const ideas = useQuery(rivyuConvexIdeaPadStats, {}) as RivyuIdeaPadStatsPublic | undefined
  const bugs = useQuery(rivyuConvexBugBountyStats, {}) as RivyuBugBountyStatsPublic | undefined
  const loading = global === undefined
  const online = pickCurrentActiveUsers(global)
  const uniqueStakers =
    global?.uniqueStakers ?? (investor != null ? investor.staking.uniqueStakers : undefined)

  return (
    <StatsNumbersGrid
      headMeta={<LiveConvexMeta updatedAt={global?.updatedAt} />}
      minersPill={<span className="pill live">◆ LIVE</span>}
      minersBig={
        loading ? (
          <span className="text-[var(--text-dim)]">···</span>
        ) : (
          <CountUp to={Math.round(online)} dur={1200} />
        )
      }
      uniqueStakers={uniqueStakers}
      communityPrograms={{ mode: "live", ideas, bugs }}
    />
  )
}

export function StatsSection({ convexUrl: convexUrlProp }: StatsSectionProps) {
  const convexUrl = (convexUrlProp ?? process.env.NEXT_PUBLIC_CONVEX_URL ?? "").trim()
  if (!convexUrl) {
    return <StatsSectionStatic />
  }
  return (
    <ConvexUrlProvider url={convexUrl}>
      <StatsSectionLiveInner />
    </ConvexUrlProvider>
  )
}
